import React, { useState, useEffect, useCallback } from 'react'
import { QueryClient } from '@tanstack/react-query'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister'
import AsyncStorage from '@react-native-async-storage/async-storage'
import { SafeAreaProvider } from 'react-native-safe-area-context'
import { GestureHandlerRootView } from 'react-native-gesture-handler'
import { LanguageProvider } from '@context'
import { ModalProvider } from '@context'
import { AuthProvider } from '@context'
import { SetupAxiosInterceptors } from '@context'
import { CreationStackProvider } from '@context'
import { PaywallProvider } from '@context'
import { CreationStackModal } from '@components'
//import { CustomModal } from '@components'
import Toast from 'react-native-toast-message'
import { toastConfig } from '@utils'
import { MainNavigator } from '@navigation'
import { navigationRef } from './navigationRef'
import { WebLayout } from './navigation/web/WebLayout'
import { NavigationContainer } from '@react-navigation/native'
import { linkingConfig } from './navigation/web/linkingConfig'
import { useDarkModeStore } from '@store'
import { usePurchaseIdentity } from '@hooks'
import { darkTheme, lightTheme } from '@theme'
import { Platform } from 'react-native'
import * as Sentry from '@sentry/react';

import { 
  browserTracingIntegration,
  replayIntegration
 } from '@sentry/browser';
import Config from '@config'

// Side-effect-only child: syncs the RevenueCat Web Billing app user id with the
// signed-in account. Must live inside AuthProvider so the current user is known.
const PurchaseIdentitySync = (): null => {
  usePurchaseIdentity()
  return null
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
    },
  },
})
const persister = createAsyncStoragePersister({ storage: AsyncStorage })

Sentry.init({
  dsn: typeof window !== 'undefined' && window.location?.hostname === 'localhost' ? undefined : Config.SENTRY_WEB_DSN,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  integrations: [browserTracingIntegration(), replayIntegration()],
  tracesSampleRate: 0.1,
  tracePropagationTargets: ["localhost", /^https:\/\/keeptouch-api-365601658222.europe-west1.run\.app\/api/],
  // GDPR / data-minimization: do NOT auto-attach IP, cookies, headers.
  sendDefaultPii: false,
  enableLogs: true,
});

const App = () => {
  const [appIsReady, setAppIsReady] = useState(false)
  const { darkMode } = useDarkModeStore()

  useEffect(() => {
    Promise.all([
      new Promise((resolve) => setTimeout(resolve, 1000))
    ])
      .then(() => setAppIsReady(true))
      .catch((e) => console.warn(e))
  }, [])


  const onLayoutRootView = useCallback(async () => {
    if (appIsReady) {
      // No splash screen on web
    }
  }, [appIsReady])


  if (!appIsReady) {
    return null
  }

  return (
    <GestureHandlerRootView style={{ flex: 1 }} onLayout={onLayoutRootView}>
      <PersistQueryClientProvider 
        client={queryClient} 
        persistOptions={{ persister }} 
        onSuccess={() => {
          queryClient.resumePausedMutations().then(() => {
            queryClient.invalidateQueries()
          })
        }}
      >
        <LanguageProvider>
          <SafeAreaProvider>
            <ModalProvider>
              <CreationStackProvider>
                <NavigationContainer
                  ref={navigationRef}
                  theme={darkMode ? darkTheme : lightTheme}
                  linking={Platform.OS === 'web' ? linkingConfig : undefined}
                >
                  <AuthProvider>
                    <PaywallProvider>
                      <SetupAxiosInterceptors />
                      <PurchaseIdentitySync />
                      <WebLayout>
                        <MainNavigator />
                      </WebLayout>
                      <CreationStackModal />
                      {/* <CustomModal /> */}
                      <Toast config={toastConfig} position="top" topOffset={16} />
                    </PaywallProvider>
                  </AuthProvider>
                </NavigationContainer>
              </CreationStackProvider>
            </ModalProvider>
          </SafeAreaProvider>
        </LanguageProvider>
      </PersistQueryClientProvider>
    </GestureHandlerRootView>
  )
}

export default App
